import { useEffect, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Plus, Server, Trash2, Users } from "lucide-react";
import { TokenCount, connectionLabel, displayHost, shareMe, type BotUsage } from "@timibot/core";
import { useApp } from "../../context";
import { useObserve } from "../../state/observable";
import { Preferences, connectionID, isGuestProfile, type ServerProfile } from "../../state/persistence";
import { Dialog, Menu, Sheet, StatusDot, useLongPress, useMenu } from "../../ui/components";
import { ModelPickerSheet } from "../chat/ModelPicker";
import { InstallHint, installState } from "./Install";
import { dictationSupported } from "../chat/dictation";
import "./settings.css";

type Page = "main" | "connections" | "status" | "install";

/**
 * 设置（对应 iOS 的 SettingsView）：上面是常用的（模型、通用、高级），连接明细收进“连接”二级页；
 * 需要用户动手的“切换配对 / 退出登录”统一放在最底下。
 */
export function SettingsView({ open, onClose }: { open: boolean; onClose: () => void }) {
  const app = useApp();
  useObserve(app.profiles);
  const [page, setPage] = useState<Page>("main");
  const [notify, setNotify] = useState(Preferences.notifyOnCompletion);
  const [permission, setPermission] = useState<string>(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [theme, setTheme] = useState(Preferences.theme);
  const [voice, setVoice] = useState(Preferences.voiceEnabled);
  const [quota, setQuota] = useState<BotUsage[]>([]);
  const [showModels, setShowModels] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPage("main");
    if (app.isGuest && app.api) shareMe(app.api).then((me) => setQuota(me.usage), () => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setNotifyOn = async (on: boolean) => {
    setNotify(on);
    Preferences.notifyOnCompletion = on;
    if (on) setPermission(await app.requestNotificationPermission());
  };

  const applyTheme = (t: "system" | "light" | "dark") => {
    setTheme(t);
    Preferences.theme = t;
    if (t === "system") delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = t;
  };

  const install = installState();
  const titles: Record<Page, string> = { main: "设置", connections: "切换配对", status: "连接", install: "添加到主屏幕" };

  return (
    <Sheet open={open} onClose={onClose} title={titles[page]}
      leading={page !== "main" ? <button className="sheet-done row" onClick={() => setPage("main")}><ChevronLeft size={18} />返回</button> : undefined}>
      {page === "main" && (
        <div className="form">
          {!app.isGuest && (
            <Group title="模型">
              <Row title="模型与服务" value={app.profiles.profile(app.selectedProfile)?.model ?? ""} onClick={() => setShowModels(true)} />
            </Group>
          )}
          {app.isGuest && quota.length > 0 && (
            <Group title="本月额度">
              {quota.map((u) => <Row key={u.profile} title={app.profiles.title(u.profile === "default" ? undefined : u.profile)} value={TokenCount.usage(u)} />)}
            </Group>
          )}
          <Group title="通用" footer={permission === "denied" ? "浏览器已禁止这个网站发通知，请在浏览器的网站设置里允许。" : "页面在后台时，任务完成或需要你确认会弹出提醒。"}>
            <label className="settings-row">
              <span>任务完成时通知</span>
              <input type="checkbox" className="switch" checked={notify && permission !== "denied"} disabled={permission === "unsupported"} onChange={(e) => void setNotifyOn(e.target.checked)} />
            </label>
            <label className="settings-row">
              <span>外观</span>
              <select className="select" value={theme} onChange={(e) => applyTheme(e.target.value as "system" | "light" | "dark")}>
                <option value="system">跟随系统</option>
                <option value="light">浅色</option>
                <option value="dark">深色</option>
              </select>
            </label>
          </Group>
          <Group title="语音输入" footer={dictationSupported()
            ? "用浏览器自带的语音识别：iPhone 上的 Safari 用苹果的服务，电脑 Chrome 用谷歌的服务（国内网络可能用不了），Edge 用微软的服务。识别的字直接写进输入框。"
            : "这个浏览器不支持语音识别。iPhone 请用 Safari，电脑可以用 Chrome、Edge 或 Safari。"}>
            <label className="settings-row">
              <span>输入框里显示麦克风</span>
              <input type="checkbox" className="switch" checked={voice && dictationSupported()} disabled={!dictationSupported()}
                onChange={(e) => { setVoice(e.target.checked); Preferences.voiceEnabled = e.target.checked; app.touch(); }} />
            </label>
          </Group>
          <Group title="高级">
            <Row title="机器人" value={app.profiles.profiles.length > 1 ? `${app.profiles.profiles.length} 个` : ""} onClick={() => app.present("bots")} />
            {!app.isDemo && (
              <Row title="连接" valueNode={<span className="row" style={{ gap: 6 }}><StatusDot state={app.connection} />{connectionLabel(app.connection)}</span>} onClick={() => setPage("status")} />
            )}
            {install !== "installed" && <Row title="添加到主屏幕" value={install === "prompt" ? "可以安装" : ""} onClick={() => setPage("install")} />}
          </Group>
          <Group footer={`TimiBot 网页版 ${__APP_VERSION__}`}>
            {app.isDemo
              ? <button className="settings-row t-brand" onClick={() => app.presentConnect("add")}>连接我的 Hermes</button>
              : <Row title="切换配对" onClick={() => setPage("connections")} />}
            <button className="settings-row t-danger" onClick={() => setConfirmSignOut(true)}>{app.isDemo ? "退出体验模式" : "退出登录"}</button>
          </Group>
        </div>
      )}
      {page === "connections" && <Connections onClose={onClose} />}
      {page === "status" && <ConnectionStatus />}
      {page === "install" && <div className="form"><InstallHint /></div>}
      <ModelPickerSheet open={showModels} onClose={() => setShowModels(false)} bot={{ profile: app.selectedProfile }} title="模型与服务" />
      <Dialog open={confirmSignOut} title={app.isDemo ? "退出体验模式？" : "退出登录？"}
        message={app.isDemo ? "回到连接页面。" : "这个浏览器会忘掉当前连接，主机上的对话不受影响。"}
        onClose={() => setConfirmSignOut(false)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "退出", role: "destructive", onClick: () => { onClose(); app.signOut(); } }]} />
    </Sheet>
  );
}

function Group({ title, footer, children }: { title?: string; footer?: string; children: React.ReactNode }) {
  return (
    <div className="form-group">
      {title && <span className="form-label">{title}</span>}
      <div className="form-card">{children}</div>
      {footer && <span className="t-caption t-2 form-hint">{footer}</span>}
    </div>
  );
}

function Row({ title, value, valueNode, onClick }: { title: string; value?: string; valueNode?: React.ReactNode; onClick?: () => void }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className="settings-row" onClick={onClick}>
      <span>{title}</span>
      <span className="row t-2" style={{ gap: 6, minWidth: 0 }}>
        {valueNode ?? <span className="nowrap">{value}</span>}
        {onClick && <ChevronRight size={14} className="t-3" />}
      </span>
    </Tag>
  );
}

/** 切换配对：自己的主机，以及朋友分享的 Bot。点一下切换，右键（长按）删除不用的 */
function Connections({ onClose }: { onClose: () => void }) {
  const app = useApp();
  const menu = useMenu();
  const [target, setTarget] = useState<ServerProfile>();
  return (
    <div className="form">
      <div className="form-card">
        {app.server && <ConnectionRow server={app.server} current />}
        {app.savedServers.map((s) => (
          <ConnectionRow key={connectionID(s)} server={s} onClick={() => { app.switchConnection(s); onClose(); }} onMenu={(pos) => { setTarget(s); menu.show(pos); }} />
        ))}
      </div>
      <div className="form-card">
        <button className="settings-row t-brand" onClick={() => app.presentConnect("add")}><span className="row" style={{ gap: 8 }}><Plus size={17} />添加配对</span></button>
      </div>
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={160}
        items={[{ label: "删除", icon: <Trash2 size={16} />, destructive: true, onSelect: () => { if (target) app.forgetConnection(target); } }]} />
    </div>
  );
}

function ConnectionRow({ server, current, onClick, onMenu }: { server: ServerProfile; current?: boolean; onClick?: () => void; onMenu?: (pos: { x: number; y: number }) => void }) {
  const press = useLongPress((pos) => onMenu?.(pos));
  const guest = isGuestProfile(server);
  return (
    <button className="connection-row" onClick={onClick} disabled={current} {...(onMenu ? press : {})}>
      {guest ? <Users size={20} className="t-brand" /> : <Server size={20} className="t-2" />}
      <span className="col" style={{ flex: 1, minWidth: 0, gap: 2, textAlign: "left" }}>
        <span className="nowrap">{displayHost(server.endpoint)}</span>
        <span className="t-caption t-2">{guest ? "朋友分享的 Bot" : "我的主机"}</span>
      </span>
      {current && <Check size={18} strokeWidth={2.6} className="t-brand" />}
    </button>
  );
}

/** 连接明细：排查问题时才需要看 */
function ConnectionStatus() {
  const app = useApp();
  const d = app.lastConnectDuration;
  return (
    <div className="form">
      <div className="form-card">
        <Row title="主机" value={displayHost(app.server?.endpoint) || "-"} />
        <Row title="状态" valueNode={<span className="row" style={{ gap: 6 }}><StatusDot state={app.connection} />{connectionLabel(app.connection)}</span>} />
        {d !== undefined && <Row title="连接耗时" value={d < 1000 ? `${d} 毫秒` : `${(d / 1000).toFixed(1)} 秒`} />}
        {app.serverStatus?.version && <Row title="Hermes" value={`v${app.serverStatus.version}`} />}
        {app.hostWelcome?.connector && <Row title="connector" value={`v${app.hostWelcome.connector}`} />}
        {app.serverStatus?.gateway_running !== undefined && <Row title="消息网关" value={app.serverStatus.gateway_running ? "运行中" : "未运行"} />}
        <Row title="加密" value="端到端（X25519 + AES-GCM）" />
      </div>
      <div className="form-card"><button className="settings-row t-brand" onClick={() => app.retryNow()}>立即重连</button></div>
    </div>
  );
}
